import React from 'react';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const BusinessResponsibility = ({ 
  responsibilities, 
  onResponsibilitiesChange,
  budgetRange,
  onBudgetRangeChange,
  revenueImpact,
  onRevenueImpactChange
}) => {
  const responsibilityOptions = [
    { id: 'budget', label: 'Budget Management', description: 'Managing departmental or project budgets' },
    { id: 'revenue', label: 'Revenue Responsibility', description: 'Direct impact on company revenue' },
    { id: 'pl', label: 'P&L Ownership', description: 'Profit and loss responsibility' },
    { id: 'strategic', label: 'Strategic Planning', description: 'Contributing to company strategy' },
    { id: 'vendor', label: 'Vendor Management', description: 'Managing external partnerships' },
    { id: 'hiring', label: 'Hiring Decisions', description: 'Authority in recruitment decisions' }
  ];

  const budgetRangeOptions = [
    { value: 'none', label: 'No budget responsibility' },
    { value: '0-100k', label: '$0 - $100K' },
    { value: '100k-500k', label: '$100K - $500K' },
    { value: '500k-1m', label: '$500K - $1M' },
    { value: '1m-5m', label: '$1M - $5M' },
    { value: '5m-10m', label: '$5M - $10M' },
    { value: '10m+', label: '$10M+' }
  ];

  const revenueImpactOptions = [
    { value: 'none', label: 'No direct revenue impact' },
    { value: 'indirect', label: 'Indirect revenue impact' },
    { value: '0-1m', label: '$0 - $1M annual impact' },
    { value: '1m-5m', label: '$1M - $5M annual impact' },
    { value: '5m-10m', label: '$5M - $10M annual impact' },
    { value: '10m+', label: '$10M+ annual impact' }
  ];

  const handleResponsibilityToggle = (id) => {
    const newResponsibilities = responsibilities?.includes(id)
      ? responsibilities?.filter(r => r !== id)
      : [...responsibilities, id];
    onResponsibilitiesChange(newResponsibilities);
  };

  const showBudgetDetails = responsibilities?.includes('budget') || responsibilities?.includes('pl');
  const showRevenueDetails = responsibilities?.includes('revenue') || responsibilities?.includes('pl');

  return (
    <div className="w-full">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
        Business Responsibility
      </h2>
      <p className="text-sm md:text-base lg:text-lg text-muted-foreground mb-6 md:mb-8">
        Select all areas where you have direct business responsibility
      </p>
      <div className="space-y-6">
        <CheckboxGroup label="Areas of Responsibility">
          {responsibilityOptions?.map((option) => (
            <Checkbox
              key={option?.id}
              label={option?.label}
              description={option?.description}
              checked={responsibilities?.includes(option?.id)}
              onChange={() => handleResponsibilityToggle(option?.id)}
            />
          ))}
        </CheckboxGroup>

        {showBudgetDetails && (
          <Select
            label="Budget Management Range"
            description="Annual budget you manage or influence"
            options={budgetRangeOptions}
            value={budgetRange}
            onChange={onBudgetRangeChange}
            required
          />
        )}

        {showRevenueDetails && (
          <Select
            label="Revenue Impact"
            description="Your direct or indirect impact on company revenue"
            options={revenueImpactOptions}
            value={revenueImpact}
            onChange={onRevenueImpactChange}
            required
          />
        )}

        <div className="p-4 md:p-6 rounded-xl bg-accent/10 border border-accent/20">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                <span className="text-lg">💼</span>
              </div>
            </div>
            <div>
              <p className="text-sm md:text-base text-foreground font-medium mb-1">
                Business Impact Matters
              </p>
              <p className="text-sm md:text-base text-muted-foreground">
                Understanding your business responsibilities helps us match you with roles that align with your experience level and impact scope.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessResponsibility;